import {
  CheckCircle2,
  Code2,
  FileText,
  Languages,
  LoaderCircle,
  Save,
} from 'lucide-react';
import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useAlerts } from '../../components/feedback/AlertProvider';
import { LEGAL_PAGES } from '../../config/legalPages';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  getLegalPages,
  updateLegalPage,
} from '../../api/legalPageApi';

import '../../styles/LegalPages/LegalPages.css';

const EDITOR_LANGUAGES = [
  { code: 'fr', short: 'FR', labelKey: 'languages.fr' },
  { code: 'en', short: 'EN', labelKey: 'languages.en' },
  { code: 'ar', short: 'AR', labelKey: 'languages.ar' },
  { code: 'es', short: 'ES', labelKey: 'languages.es' },
];

// Builds an empty local page when the API has not returned one yet.
function createEmptyPage(slug) {
  return {
    slug,
    content_fr: '',
    content_en: '',
    content_ar: '',
    content_es: '',
    is_active: true,
  };
}

export default function LegalPagesManager() {
  const { showAlert } = useAlerts();
  const { t } = useLanguage();
  const [pages, setPages] = useState([]);
  const [allowedHtmlTags, setAllowedHtmlTags] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(LEGAL_PAGES[0].slug);
  const [editorLanguage, setEditorLanguage] = useState('fr');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(
    // Loads all editable legal pages for the Super Admin.
    function loadPages() {
      let active = true;

      setLoading(true);

      getLegalPages()
        .then((result) => {
          if (!active) {
            return;
          }

          setPages(Array.isArray(result?.pages) ? result.pages : []);
          setAllowedHtmlTags(
            Array.isArray(result?.allowedHtmlTags)
              ? result.allowedHtmlTags
              : [],
          );
        })
        .catch((error) => {
          if (active) {
            showAlert({
              type: 'error',
              message:
                error?.message || t('legalPages.admin.loadError'),
            });
          }
        })
        .finally(() => {
          if (active) {
            setLoading(false);
          }
        });

      return () => {
        active = false;
      };
    },
    [showAlert, t],
  );

  const selectedDefinition = useMemo(
    () =>
      LEGAL_PAGES.find((page) => page.slug === selectedSlug) ||
      LEGAL_PAGES[0],
    [selectedSlug],
  );

  const selectedPage = useMemo(
    () =>
      pages.find((page) => page.slug === selectedSlug) ||
      createEmptyPage(selectedSlug),
    [pages, selectedSlug],
  );

  const editorField = `content_${editorLanguage}`;
  const editorValue = selectedPage[editorField] || '';
  const SelectedIcon = selectedDefinition.icon;

  // Updates one translated HTML draft locally without saving immediately.
  function updateEditorValue(event) {
    updateSelectedPage({
      [editorField]: event.target.value,
    });
  }

  // Updates one field of the selected local page.
  function updateSelectedPage(changes) {
    setPages((currentPages) => {
      const exists = currentPages.some(
        (page) => page.slug === selectedSlug,
      );

      if (!exists) {
        return [
          ...currentPages,
          {
            ...createEmptyPage(selectedSlug),
            ...changes,
          },
        ];
      }

      return currentPages.map((page) =>
        page.slug === selectedSlug
          ? { ...page, ...changes }
          : page,
      );
    });
  }

  // Persists all language versions of the selected legal page.
  async function saveCurrentPage() {
    if (saving) {
      return;
    }

    setSaving(true);

    try {
      const result = await updateLegalPage(selectedSlug, {
        content_fr: selectedPage.content_fr || '',
        content_en: selectedPage.content_en || '',
        content_ar: selectedPage.content_ar || '',
        content_es: selectedPage.content_es || '',
      });

      if (result?.page) {
        setPages((currentPages) => {
          const exists = currentPages.some(
            (page) => page.slug === selectedSlug,
          );

          if (!exists) {
            return [...currentPages, result.page];
          }

          return currentPages.map((page) =>
            page.slug === selectedSlug ? result.page : page,
          );
        });
      }

      showAlert({
        type: 'success',
        message:
          result?.message || t('legalPages.admin.saveSuccess'),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        message:
          error?.message || t('legalPages.admin.saveError'),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="legal-admin-page">
      <header className="legal-admin-heading">
        <div className="legal-admin-heading-icon">
          <FileText size={24} />
        </div>

        <div>
          <span>{t('legalPages.admin.kicker')}</span>
          <h1>{t('legalPages.admin.title')}</h1>
          <p>{t('legalPages.admin.subtitle')}</p>
        </div>
      </header>

      {loading ? (
        <div className="legal-admin-loading" role="status">
          <LoaderCircle className="legal-spin" size={30} />
          <span>{t('legalPages.admin.loading')}</span>
        </div>
      ) : (
        <div className="legal-admin-layout">
          <aside
            className="legal-admin-list"
            aria-label={t('legalPages.admin.pageList')}
          >
            <div className="legal-admin-list-header">
              <strong>{t('legalPages.admin.pages')}</strong>
              <span>{LEGAL_PAGES.length}</span>
            </div>

            {LEGAL_PAGES.map((definition) => {
              const PageIcon = definition.icon;
              const page = pages.find(
                (item) => item.slug === definition.slug,
              );
              const translatedCount = EDITOR_LANGUAGES.filter(
                ({ code }) => Boolean(page?.[`content_${code}`]?.trim()),
              ).length;
              const isActive = selectedSlug === definition.slug;

              return (
                <button
                  key={definition.slug}
                  type="button"
                  className={`legal-admin-list-item${
                    isActive ? ' active' : ''
                  }`}
                  onClick={() => setSelectedSlug(definition.slug)}
                >
                  <span className="legal-admin-list-icon">
                    <PageIcon size={18} />
                  </span>

                  <span className="legal-admin-list-copy">
                    <strong>{t(definition.labelKey)}</strong>
                    <small>
                      {t('legalPages.admin.translationCount', {
                        count: translatedCount,
                        total: EDITOR_LANGUAGES.length,
                      })}
                    </small>
                  </span>

                  {translatedCount === EDITOR_LANGUAGES.length ? (
                    <CheckCircle2
                      size={17}
                      className="legal-admin-complete-icon"
                    />
                  ) : null}
                </button>
              );
            })}
          </aside>

          <section className="legal-admin-editor">
            <div className="legal-admin-editor-header">
              <div className="legal-admin-editor-title">
                <span className="legal-admin-editor-icon">
                  <SelectedIcon size={20} />
                </span>

                <div>
                  <span>{t('legalPages.admin.editing')}</span>
                  <h2>{t(selectedDefinition.labelKey)}</h2>
                </div>
              </div>

              <button
                type="button"
                className="legal-admin-save-button"
                onClick={saveCurrentPage}
                disabled={saving}
              >
                {saving ? (
                  <LoaderCircle className="legal-spin" size={17} />
                ) : (
                  <Save size={17} />
                )}
                {saving
                  ? t('legalPages.admin.saving')
                  : t('legalPages.admin.save')}
              </button>
            </div>

            <div className="legal-admin-language-tabs" role="tablist">
              <span className="legal-admin-language-label">
                <Languages size={16} />
                {t('legalPages.admin.language')}
              </span>

              {EDITOR_LANGUAGES.map((item) => (
                <button
                  key={item.code}
                  type="button"
                  role="tab"
                  aria-selected={editorLanguage === item.code}
                  className={editorLanguage === item.code ? 'active' : ''}
                  onClick={() => setEditorLanguage(item.code)}
                >
                  <strong>{item.short}</strong>
                  <span>{t(item.labelKey)}</span>
                </button>
              ))}
            </div>

            <div className="legal-admin-html-note">
              <Code2 size={18} />
              <div>
                <strong>{t('legalPages.admin.htmlTitle')}</strong>
                <span>{t('legalPages.admin.htmlDescription')}</span>

                {allowedHtmlTags.length > 0 ? (
                  <div className="legal-admin-tag-list">
                    {allowedHtmlTags.map((tag) => (
                      <code key={tag}>{`<${tag}>`}</code>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>

            <label
              className="legal-admin-textarea-label"
              htmlFor="legal-page-html"
            >
              <span>{t('legalPages.admin.contentLabel')}</span>
              <small>
                {editorValue.length.toLocaleString()} / 200000
              </small>
            </label>

            <textarea
              id="legal-page-html"
              className="legal-admin-textarea"
              dir={editorLanguage === 'ar' ? 'rtl' : 'ltr'}
              value={editorValue}
              onChange={updateEditorValue}
              placeholder={t('legalPages.admin.placeholder')}
              spellCheck="false"
              maxLength={200000}
            />
          </section>
        </div>
      )}
    </div>
  );
}